import {type FC} from 'react'
import classNames from 'classnames'

import {Button} from 'design'

export interface DopamineButtonProps {
  count: number | string
  ariaLabel: string
  label?: string
  isPressed?: boolean
  isDisabled?: boolean
  onClick?: () => void
  dataCy?: string
  countDataCy?: string
  className?: string
  tooltipId?: string
  tooltipContent?: string
}

export const DopamineButton: FC<DopamineButtonProps> = ({
  count,
  ariaLabel,
  label = 'Dopamine',
  isPressed = false,
  isDisabled = false,
  onClick,
  dataCy = 'dopamine-button',
  countDataCy = 'dopamine-button-count',
  className,
  tooltipId,
  tooltipContent,
}) => {
  const tooltipProps =
    tooltipId && tooltipContent
      ? {
          'data-tooltip-id': tooltipId,
          'data-tooltip-content': tooltipContent,
        }
      : {}

  return (
    <Button
      type="button"
      variant="noStyle"
      size="zero"
      colorScheme="noStyle"
      className={classNames(
        'relative flex h-8 items-center gap-2 rounded-sm border py-1.5 px-2.5 ml-1',
        'border-dope-purple-500/30 bg-dope-purple-500/20',
        'hover:border-dope-purple-50/40 hover:bg-dope-purple-500/25',
        'active:border-dope-purple-50/60 active:bg-dope-purple-500/35',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dope-purple-50/50',
        {
          'overflow-hidden border-dope-purple-50 bg-dope-purple-500/35 hover:bg-dope-purple-500/40 active:bg-dope-purple-500/45 before:pointer-events-none before:absolute before:inset-0 before:z-0 before:bg-gradient-to-r before:from-transparent before:via-dope-purple-50/25 before:to-transparent before:animate-reflection-sweep':
            isPressed,
        },
        className,
      )}
      data-cy={dataCy}
      aria-label={ariaLabel}
      aria-pressed={isPressed}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      onClick={onClick}
      {...tooltipProps}
    >
      <span className="relative z-1 abc-bold text-11px uppercase tracking-[1.1px] text-dope-purple-50">
        {label}
      </span>
      <span
        className={classNames(
          'relative z-1 flex h-full items-center justify-center self-stretch rounded-md p-1.5 abc-bold text-10px text-white',
          isPressed ? 'bg-dope-purple-400' : 'bg-dope-purple-500',
        )}
        data-cy={countDataCy}
      >
        {count}
      </span>
    </Button>
  )
}
